/**
 * Supabase data helpers for fetching data from the API
 * Real-time subscriptions are handled by subscriptionFactory.ts
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import { api } from '../global/API/apiClient';
import { productsApi, storesApi, clientsApi } from '../global/API/typedApiClient';
import { readWholeList, newestFirst } from '../global/API/readWholeList';
import * as Sentry from '@sentry/react-native';
import { SupabaseFetchError } from './supabaseFetchError';

/**
 * ATL-970 — report a failed read and produce the error to throw for it.
 *
 * Every fetcher below used to `catch`, report, and `return {}`. That value is identical to the one
 * a genuinely empty collection produces, so no caller could tell "this organization has no
 * products" from "we could not find out". A till came up with an empty catalog, logged
 * `Supabase data loaded successfully`, and said nothing.
 *
 * The Sentry shape is unchanged on purpose — `supabase_fetch_error` + `collection` are queried by
 * the migration runbooks. What changed is that the failure now also reaches the app's control flow.
 *
 * Returns the error rather than throwing it so call sites read `throw fetchFailure(...)`, which
 * keeps the control flow visible at the catch. An error that is ALREADY a `SupabaseFetchError` is
 * passed through untouched: it has been reported once, under the innermost (most specific)
 * collection, and re-wrapping it would rename that collection to its caller's.
 */
const fetchFailure = (collection, error, extra = {}) => {
  if (error instanceof SupabaseFetchError) return error;
  console.error(`Error fetching ${collection} from Supabase:`, error);
  Sentry.captureException(error, {
    tags: { supabase_fetch_error: true, collection },
    extra: {
      ...extra,
      errorMessage: error?.message,
      statusCode: error?.response?.status,
      // ATL-2035: the request context the apiClient interceptor used to report, now that this is
      // the only report of the failure (`readApi` below).
      url: error?.config?.url,
      method: error?.config?.method,
    },
  });
  const failure = new SupabaseFetchError(collection, error);
  // ATL-2035: reported once, here. `beforeSend` drops any later capture of this object, which is
  // what every caller that re-reported it (AppStack's daily-stats catch) produced: event #2.
  failure.reportedToSentry = true;
  return failure;
};

/**
 * ATL-2035 — the raw `api`, declaring that the caller reports its own failures.
 *
 * Every helper in this file catches into `fetchFailure`, which reports with the
 * `supabase_fetch_error` + `collection` tags the migration runbooks query. Through the plain `api`
 * the apiClient interceptor ALSO reported the same axios error first — so the SDK's
 * already-captured check (object identity) silently skipped `fetchFailure`'s capture, and those
 * runbook tags never arrived for any of these collections (a false zero for
 * `supabase_fetch_error:True collection:daily_stats`). One reporter per failure: this one.
 */
const readApi = {
  get: (url, config = {}) => api.get(url, { ...config, callerReportsFailure: true }),
};

/**
 * Convert array to dictionary keyed by specified field
 * @param {Array<Object>} items - Array of objects to convert
 * @param {string} keyField - Field name to use as dictionary key
 * @returns {Object} Dictionary of items keyed by specified field
 */
const arrayToDict = (items, keyField = 'id') => {
  if (!items || !Array.isArray(items)) return {};
  return items.reduce((dict, item) => {
    const key = item[keyField] || item.id;
    if (key) dict[key] = item;
    return dict;
  }, {});
};

/**
 * Fetch products from Supabase API
 * @param {string} organizationId - Organization ID
 * @param {string} storeId - Optional store ID filter
 * @returns {Promise<Object>} Dictionary of products keyed by ID
 */
export const fetchProductsFromSupabase = async (organizationId, storeId = null) => {
  try {
    const params = {};
    if (storeId) {
      params.storeId = storeId;
    }

    // Using typed API - returns ProductResponse[] directly (validated)
    // Backend returns fully-transformed camelCase data matching Firestore format
    const products = await productsApi.getProducts(params);

    // Use firestoreId as the key to match ref.id from Firestore operations
    // (similar to how stores use firestoreId as key)
    return arrayToDict(products, 'firestoreId');
  } catch (error) {
    throw fetchFailure('products', error, { organizationId, storeId });
  }
};

/**
 * Fetch product categories from Supabase API
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Dictionary of categories keyed by firestoreId
 */
export const fetchProductCategoriesFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/product-categories', {
      params: { organizationId },
    });
    const categories = response.data || [];

    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    return arrayToDict(categories, 'firestoreId');
  } catch (error) {
    throw fetchFailure('product_categories', error, { organizationId });
  }
};

/**
 * Fetch stores from Supabase API
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Dictionary of stores keyed by firestoreId
 */
export const fetchStoresFromSupabase = async (organizationId) => {
  try {
    // Using typed API - returns StoreResponse[] directly (validated)
    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    const stores = await storesApi.getStores();

    // Use firestoreId as the key to match user.store.id (which comes from Firestore)
    // Pass identity function since backend already transforms the data
    return arrayToDict(stores, 'firestoreId');
  } catch (error) {
    throw fetchFailure('stores', error, { organizationId });
  }
};

/**
 * Fetch users from Supabase API
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Dictionary of users keyed by firestoreId
 */
export const fetchUsersFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/users', {
      params: { organizationId },
    });
    const users = response.data || [];

    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    // Use firestoreId (Firebase Auth UID) as key for compatibility with Firestore saved carts
    return arrayToDict(users, 'firestoreId');
  } catch (error) {
    throw fetchFailure('users', error, { organizationId });
  }
};

/**
 * Fetch roles from Supabase API
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Dictionary of roles keyed by firestoreId
 */
export const fetchRolesFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/roles', {
      params: { organizationId },
    });
    const roles = response.data || [];

    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    return arrayToDict(roles, 'firestoreId');
  } catch (error) {
    throw fetchFailure('roles', error, { organizationId });
  }
};

/**
 * Fetch payment methods from Supabase API
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Dictionary of payment methods keyed by firestoreId
 */
export const fetchPaymentMethodsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/payment-methods', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('payment_methods', error);
  }
};

/**
 * Fetch purchase methods from Supabase API
 */
export const fetchPurchaseMethodsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/purchase-methods', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('purchase_methods', error);
  }
};

/**
 * Fetch product units from Supabase API
 */
export const fetchProductUnitsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/product-units', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('organization_product_units', error);
  }
};

/**
 * Fetch wallets from Supabase API
 */
export const fetchWalletsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/wallets', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('wallets', error);
  }
};

/**
 * Fetch discount options from Supabase API
 */
export const fetchDiscountOptionsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/discount-options', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('discount_options', error);
  }
};

/**
 * Fetch spending categories from Supabase API
 */
export const fetchSpendingCategoriesFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/spending-categories', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('spending_categories', error);
  }
};

/**
 * Fetch write off reasons from Supabase API
 */
export const fetchWriteOffReasonsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/write-off-reasons', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('write_off_reasons', error);
  }
};

/**
 * Fetch return reasons from Supabase API
 */
export const fetchReturnReasonsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/return-reasons', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('return_reasons', error);
  }
};

/**
 * Fetch price types from Supabase API
 */
export const fetchPriceTypesFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/price-types', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('price_types', error);
  }
};

/**
 * Fetch product modifiers from Supabase API
 */
export const fetchProductModifiersFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/product-modifiers', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('product_modifiers', error);
  }
};

/**
 * Fetch sale settings from Supabase API
 * Note: sale_settings is keyed by store_id, not firestore_id
 */
export const fetchSaleSettingsFromSupabase = async (organizationId) => {
  console.log('[fetchSaleSettingsFromSupabase] START - organizationId:', organizationId);
  try {
    const response = await readApi.get('/sale-settings', {
      params: { organizationId },
    });
    const items = response.data || [];
    console.log('[fetchSaleSettingsFromSupabase] Raw API response items:', JSON.stringify(sanitizeForFirestore(items), null, 2));
    // Key by storeId since sale_settings is store-specific
    const result = arrayToDict(items, 'storeId');
    console.log('[fetchSaleSettingsFromSupabase] Result dict (keyed by storeId):', JSON.stringify(sanitizeForFirestore(result), null, 2));
    return result;
  } catch (error) {
    throw fetchFailure('sale_settings', error);
  }
};

/**
 * Fetch loyalty settings from Supabase API
 * Note: loyalty_settings is one-per-organization (returns single object or null)
 */
export const fetchLoyaltySettingsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/loyalty-settings', {
      params: { organizationId },
    });
    // Returns single object or null, not array
    return response.data || null;
  } catch (error) {
    throw fetchFailure('loyalty_settings', error);
  }
};

/**
 * Fetch recurring spendings from Supabase API
 */
export const fetchRecurringSpendingsFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/recurring-spendings', {
      params: { organizationId },
    });
    const items = response.data || [];
    // ATL-1990 — the realtime resync writes this result into `constants.recurringSpendings.data`,
    // the same slot `fetchConstantsFromSupabase` fills at start-up. It must therefore produce the
    // SAME shape: raw axios hands back ISO strings, and a screen that calls `date.toDate()` threw
    // on every row after the app came back from the background.
    return arrayToDict(items.map(toRecurringSpendingConstant), 'firestoreId');
  } catch (error) {
    throw fetchFailure('recurring_spendings', error);
  }
};

/**
 * Fetch complex sale templates from Supabase API
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Dictionary of templates keyed by firestoreId
 */
export const fetchComplexSaleTemplatesFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/complex-sale-templates', {
      params: { organizationId },
    });
    const items = response.data || [];
    return arrayToDict(items, 'firestoreId');
  } catch (error) {
    throw fetchFailure('complex_sale_templates', error);
  }
};

/**
 * Fetch suppliers from Supabase API
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Dictionary of suppliers keyed by firestoreId
 */
export const fetchSuppliersFromSupabase = async (organizationId) => {
  try {
    const response = await readApi.get('/suppliers', {
      params: { organizationId },
    });
    const suppliers = response.data || [];

    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    return arrayToDict(suppliers, 'firestoreId');
  } catch (error) {
    throw fetchFailure('suppliers', error, { organizationId });
  }
};

/**
 * Fetch clients from Supabase API
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @param {Object} filters - Optional filters (search, phone, etc.)
 * @returns {Promise<Array>} Array of clients
 */
export const fetchClientsFromSupabase = async (organizationId, filters = {}) => {
  try {
    const params = { organizationId, ...filters };
    // Using typed API - returns ClientResponse[] directly (validated)
    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    // ATL-1477: every matching client. GET /clients returns 50 by default, so a store's
    // "bought in this period" list stopped at 50 clients with nothing to say so.
    const clients = await readWholeList(
      (window) => clientsApi.getClientsWindow({ ...params, ...window }),
      { label: 'clients', order: newestFirst('lastPurchaseDate') },
    );

    return clients;
  } catch (error) {
    throw fetchFailure('clients', error);
  }
};

/**
 * Fetch constants from Supabase API
 * Constants are fetched from backend API endpoints
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Constants object matching Firestore structure
 */
export const fetchConstantsFromSupabase = async (organizationId) => {
  try {
    // Fetch all constants from the backend API
    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    const response = await readApi.get('/settings');
    const constantsData = response.data || {};

    // Price types are not part of the aggregated /settings payload, so fetch them
    // from the dedicated endpoint. priceTypesAtom reads constants.priceTypes.data,
    // and PriceTypeSelect only renders when that array is non-empty.
    let priceTypesData = [];
    try {
      const priceTypesResponse = await readApi.get('/price-types', {
        params: { organizationId },
      });
      priceTypesData = priceTypesResponse.data || [];
    } catch (priceTypesError) {
      // ATL-970: this used to swallow to []. `PriceTypeSelect` only renders when
      // `constants.priceTypes.data` is non-empty, so a failed fetch silently removed every
      // non-default price a customer could be charged and looked exactly like an organization
      // that has no price types. The outer catch re-throws this unchanged, so the failure is
      // reported as `price_types`, not as `constants`.
      throw fetchFailure('price_types', priceTypesError, { organizationId });
    }

    // Build constants object matching Firestore structure
    // Backend returns pre-split spending categories and loyaltySettings as array
    const constants = {
      paymentMethods: {
        id: 'paymentMethods',
        organizationId,
        data: constantsData.paymentMethods || [],
        ref: createMockRef('constants', 'paymentMethods'),
      },
      purchaseMethods: {
        id: 'purchaseMethods',
        organizationId,
        data: constantsData.purchaseMethods || [],
        ref: createMockRef('constants', 'purchaseMethods'),
      },
      roles: {
        id: 'roles',
        organizationId,
        data: constantsData.roles || [],
        ref: createMockRef('constants', 'roles'),
      },
      productUnits: {
        id: 'productUnits',
        organizationId,
        data: constantsData.productUnits || [],
        ref: createMockRef('constants', 'productUnits'),
      },
      wallets: {
        id: 'wallets',
        organizationId,
        data: constantsData.wallets || [],
        ref: createMockRef('constants', 'wallets'),
      },
      discountOptions: {
        id: 'discountOptions',
        organizationId,
        data: constantsData.discountOptions || [],
        ref: createMockRef('constants', 'discountOptions'),
      },
      spendingCategories: {
        id: 'spendingCategories',
        organizationId,
        data: constantsData.spendingCategories || [],
        ref: createMockRef('constants', 'spendingCategories'),
      },
      adminSpendingCategories: {
        id: 'adminSpendingCategories',
        organizationId,
        data: constantsData.adminSpendingCategories || [],
        ref: createMockRef('constants', 'adminSpendingCategories'),
      },
      writeOffReasons: {
        id: 'writeOffReasons',
        organizationId,
        data: constantsData.writeOffReasons || [],
        ref: createMockRef('constants', 'writeOffReasons'),
      },
      returnReasons: {
        id: 'returnReasons',
        organizationId,
        data: constantsData.returnReasons || [],
        ref: createMockRef('constants', 'returnReasons'),
      },
      productModifiers: {
        id: 'productModifiers',
        organizationId,
        data: constantsData.productModifiers || [],
        ref: createMockRef('constants', 'productModifiers'),
      },
      priceTypes: {
        id: 'priceTypes',
        organizationId,
        data: priceTypesData,
        ref: createMockRef('constants', 'priceTypes'),
      },
      saleSettings: {
        id: 'saleSettings',
        organizationId,
        data: (() => {
          const saleSettingsData = constantsData.saleSettings || {};
          console.log('[fetchConstantsFromSupabase] saleSettings from API (should be dict by storeId):', JSON.stringify(sanitizeForFirestore(saleSettingsData), null, 2));
          return saleSettingsData;
        })(),
        ref: createMockRef('constants', 'saleSettings'),
      },
      // loyaltySettings is a single document, not a collection - spread directly to match Firestore format
      loyaltySettings: {
        id: 'loyaltySettings',
        organizationId,
        ref: createMockRef('constants', 'loyaltySettings'),
        ...(constantsData.loyaltySettings?.[0] || {}),
      },
      recurringSpendings: {
        id: 'recurringSpendings',
        organizationId,
        data: (constantsData.recurringSpendings || []).map(toRecurringSpendingConstant),
        ref: createMockRef('constants', 'recurringSpendings'),
      },
    };

    // ATL-1038 — the flat `constants/<key>` documents (`printChecks`, `floatingMenu`,
    // `stickerHeight`). They are stored VERBATIM as one `organization_constants` row each, so they
    // are spread whole rather than mapped field by field: `printChecks` carries printer geometry
    // (`fontSize`, `width`) on two orgs and a field-by-field copy would drop it.
    //
    // WHY THIS BLOCK EXISTS AT ALL: `NewSaleScreen` reads `constants.printChecks.value` to decide
    // whether a sale prints a receipt, and until now nothing here built that key — so an
    // organization reading from Supabase saw `undefined` and stopped printing, no matter what
    // either store held. The row had been migrated and the parity check was clean the whole time:
    // a row that ARRIVES is not the same claim as a value the app can READ.
    //
    // A key the backend does not send stays ABSENT, exactly as a missing Firestore document does,
    // rather than being fabricated as an empty object that would read as "printing off".
    for (const key of ['printChecks', 'floatingMenu', 'stickerHeight']) {
      const document = constantsData[key];
      if (document && typeof document === 'object') {
        // Shaped exactly as the Firestore listener shapes it (`snap.id`/`snap.ref` on the raw
        // document body, nothing else added) — one org's document carries its own `organizationId`
        // and inventing a second one here would be drift introduced by the reader.
        constants[key] = { ...document, id: key, ref: createMockRef('constants', key) };
      }
    }

    return constants;
  } catch (error) {
    throw fetchFailure('constants', error, { organizationId });
  }
};

/**
 * Create a mock Firestore Timestamp object from an ISO date string
 * @param {string} isoString - ISO date string
 * @returns {Object} Mock Timestamp with toDate() method
 */
const createMockTimestamp = (isoString) => {
  return {
    toDate: () => new Date(isoString),
    seconds: Math.floor(new Date(isoString).getTime() / 1000),
    nanoseconds: 0,
  };
};

/**
 * A date as the constants consumers read it: a Firestore-Timestamp-shaped value (`toDate()`),
 * which is what the Firestore listener delivers. A value that already is one passes through, so
 * normalising twice is harmless.
 */
const toConstantsTimestamp = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && typeof value.toDate === 'function') return value;
  return createMockTimestamp(value);
};

/**
 * ATL-1990 — THE shape of one `constants.recurringSpendings.data[]` item in Supabase read mode.
 *
 * Two writers fill that array: `fetchConstantsFromSupabase` (`GET /settings`, at start-up) and
 * `fetchRecurringSpendingsFromSupabase` (`GET /recurring-spendings`, on every realtime resync —
 * i.e. each time the app returns from the background). The backend sends both the same row; only
 * this function decides what the app sees, so both writers call it and nothing else.
 *
 * `date`/`endDate` become Timestamp-shaped because (a) the Firestore listener delivers Timestamps
 * and screens call `.toDate()` on them, and (b) `saveRecurringSpendings` writes the WHOLE array
 * back to Firestore through `sanitizeForFirestoreDeep`, which converts only objects with
 * `toDate` — an ISO string would be written to the write master as a string.
 */
export const toRecurringSpendingConstant = (rs) => ({
  ...rs,
  date: toConstantsTimestamp(rs.date),
  endDate: toConstantsTimestamp(rs.endDate),
});

/**
 * Create a mock ref for constants (helper function)
 * @param {string} collection - Collection name
 * @param {string} id - Document ID
 * @returns {Object} Mock ref object
 */
const createMockRef = (collection, id) => {
  return {
    id,
    path: `${collection}/${id}`,
    update: () => Promise.reject(new Error('Cannot update via Supabase ref - use API')),
    delete: () => Promise.reject(new Error('Cannot delete via Supabase ref - use API')),
    get: () => Promise.reject(new Error('Cannot get via Supabase ref - use API')),
  };
};

/**
 * Fetch daily stats from Supabase API
 * Daily stats is a view — the frontend subscribes to daily_stats_changes
 * table via Realtime and calls this function when notified of changes.
 * @param {string} organizationId - Organization ID
 * @param {string} storeId - Optional store ID filter
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Object>} Dictionary of daily stats keyed by store ID
 */
export const fetchDailyStatsFromSupabase = async (organizationId, storeId = null, startDate = null, endDate = null) => {
  // Helper to format date as YYYY-MM-DD in local timezone
  // This is important because daily_stats.date is a DATE column (no time)
  // Using toISOString() would convert to UTC which causes timezone issues
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  try {
    const params = { organizationId };
    if (storeId) params.storeId = storeId;
    if (startDate) params.startDate = formatLocalDate(startDate);
    if (endDate) params.endDate = formatLocalDate(endDate);

    const response = await readApi.get('/reports/daily-stats', { params });
    const stats = response.data || [];

    // Transform to Firestore format (dictionary keyed by store ID)
    // Backend returns camelCase data - no transformation needed
    const statsDict = {};
    stats.forEach((stat) => {
      const storeIdKey = stat.storeId || stat.store;
      if (!storeIdKey) {
        console.warn('Daily stat missing store ID:', stat);
        return;
      }

      if (!statsDict[storeIdKey]) {
        statsDict[storeIdKey] = {};
      }
      const dateKey = stat.businessDate || stat.date;
      // ATL-1725: never carry the backend's `ref`. The `daily_stats` view has no document, so the
      // backend emits `ref: { id: undefined }` (ATL-576), which arrives as `ref: {}` — truthy and not a
      // reference. `ShiftScreen` hands `dailyStats[store].ref` to `cancelShiftClosing`, whose body
      // `batch.update`s it (both SDKs throw on a non-DocumentReference) and whose P9b plan refuses it;
      // with no ref both derive the close's own business day. ATL-1358 already assumes this branch of
      // the atom has no ref at all. (`stats.ts#getStatsForDays`, the other reader of this endpoint,
      // still carries the id-less ref through verbatim — it knows the ref is unusable, and nothing
      // there hands it to a writer.)
      const statWithoutRef = { ...stat };
      delete statWithoutRef.ref;
      statsDict[storeIdKey][dateKey] = statWithoutRef;
    });

    return statsDict;
  } catch (error) {
    throw fetchFailure('daily_stats', error);
  }
};

/**
 * Fetch shift report from Supabase API (ATL-652)
 *
 * The daily_stats view only has flat org/store/day totals, so the Shift/Смена screen —
 * which reads dynamic per-wallet / per-payment-method keys (`<wallet>StartAmount`,
 * `<pm>SalesAmount`, `<key>EndAmount`, `<key>SystemEndAmount`, …) that only ever lived
 * on the Firestore `dailyStats` doc — showed all zeros in Supabase read mode. This endpoint
 * recomputes those flat keys on-read. The caller merges each row's `stats` into
 * dailyStats[storeId] so ShiftScreen works unchanged.
 *
 * @param {string} organizationId - Organization ID
 * @param {string} storeId - Optional store ID filter
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Array<{ storeId: string, supabaseStoreId: string, date: string, stats: Object }>>}
 */
export const fetchShiftReportFromSupabase = async (organizationId, storeId = null, startDate = null, endDate = null) => {
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  try {
    const params = { organizationId };
    if (storeId) params.storeId = storeId;
    if (startDate) params.startDate = formatLocalDate(startDate);
    if (endDate) params.endDate = formatLocalDate(endDate);

    const response = await readApi.get('/reports/shift', { params });
    return response.data || [];
  } catch (error) {
    throw fetchFailure('shift_report', error);
  }
};

/**
 * Fetch print templates from Supabase API.
 *
 * ATL-1038 (sibling) — returns an ARRAY, not a dict. `printTemplatesAtom` holds
 * `PrintTemplate[]` (`PrintTemplatesSchema = z.array(...)`), the Firestore listener in `AppStack`
 * sets an array, and `getPrintTemplate()` calls `templates.filter(...)` on it. This helper used to
 * return `arrayToDict(items, 'firestoreId')` and had NO call site anywhere in the app — the shape
 * it produced could not be given to the only atom it could have fed, which is why nothing ever
 * called it and why the Supabase read path had no print templates at all.
 *
 * Backend returns fully-transformed camelCase data with firestoreId, `ref: { id }` and — the field
 * that matters here — `settings` (the invoice layout, `template.content` in Supabase). See
 * `print-templates.service.ts#transformPrintTemplate`.
 *
 * @param {string} organizationId - Organization ID
 * @param {string} type - Optional template type filter
 * @returns {Promise<Array>} Array of print templates in Firestore document shape
 */
export const fetchPrintTemplatesFromSupabase = async (organizationId, type = null) => {
  try {
    const params = {};
    if (type) params.type = type;

    const response = await readApi.get('/print-templates', { params });
    return response.data || [];
  } catch (error) {
    throw fetchFailure('print_templates', error, { organizationId, type });
  }
};

/**
 * Fetch organization from Supabase API
 * Backend returns fully-transformed camelCase data with firestoreId and ref: { id }
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Organization object
 */
export const fetchOrganizationFromSupabase = async (_organizationId) => {
  try {
    const response = await readApi.get('/organizations');
    // Backend returns camelCase data with firestoreId and ref: { id } - no transformation needed
    return response.data || {};
  } catch (error) {
    throw fetchFailure('organizations', error);
  }
};

/**
 * Create a reusable subscription handler for standard entities
 * This factory creates a callback function that:
 * - On INSERT/UPDATE: refetches all data from API and updates state
 * - On DELETE: removes the item from state by key
 *
 * @param {Object} options - Handler options
 * @param {Function} options.fetchFn - Function to fetch all data from API (returns dict)
 * @param {Function} options.setStateFn - State setter function
 * @param {string} options.entityName - Entity name for logging
 * @returns {Function} Callback function for subscription
 */
export const createSubscriptionHandler = ({ fetchFn, setStateFn, entityName }) => {
  return async (eventType, ref) => {
    const deleteKey = ref?.firestore_id || ref?.id;

    if (eventType === 'INSERT' || eventType === 'UPDATE') {
      try {
        const updatedData = await fetchFn();
        setStateFn(updatedData);
      } catch (error) {
        console.error(`Failed to refetch ${entityName}:`, error);
      }
    } else if (eventType === 'DELETE' && deleteKey) {
      setStateFn((prev) => {
        const updated = { ...prev };
        delete updated[deleteKey];
        return updated;
      });
    }
  };
};

export default {
  fetchProductsFromSupabase,
  fetchProductCategoriesFromSupabase,
  fetchStoresFromSupabase,
  fetchUsersFromSupabase,
  fetchRolesFromSupabase,
  fetchPaymentMethodsFromSupabase,
  fetchPurchaseMethodsFromSupabase,
  fetchProductUnitsFromSupabase,
  fetchWalletsFromSupabase,
  fetchDiscountOptionsFromSupabase,
  fetchSpendingCategoriesFromSupabase,
  fetchWriteOffReasonsFromSupabase,
  fetchReturnReasonsFromSupabase,
  fetchPriceTypesFromSupabase,
  fetchProductModifiersFromSupabase,
  fetchSaleSettingsFromSupabase,
  fetchLoyaltySettingsFromSupabase,
  fetchRecurringSpendingsFromSupabase,
  fetchComplexSaleTemplatesFromSupabase,
  fetchSuppliersFromSupabase,
  fetchClientsFromSupabase,
  fetchConstantsFromSupabase,
  fetchDailyStatsFromSupabase,
  fetchOrganizationFromSupabase,
  fetchPrintTemplatesFromSupabase,
  createSubscriptionHandler,
};

